import { useState, type ReactNode } from "react";
import { useRouterState } from "@tanstack/react-router";
import { Household } from "@finances/shared";
import { request, RequestFailed } from "../lib/api.js";
import { useMe } from "../lib/queries.js";
import { SessionProvider, type Session } from "../lib/session.js";
import { SignIn } from "../features/auth/SignIn.js";
import { Homepage } from "../features/marketing/Homepage.js";
import {
  Alert,
  Button,
  Card,
  ErrorState,
  Field,
  Heading,
  Input,
  Skeleton,
  Stack,
  Text,
} from "../ui/index.js";
import styles from "./AuthGate.module.css";

/**
 * The boundary between "signed out" and "everything else".
 *
 * Below it, `useSession()` is guaranteed, so no screen has to render a
 * half-known user. Above it there are only three outcomes: loading, signed
 * out, and signed in but not in a household yet.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const me = useMe();
  const path = useRouterState({ select: (state) => state.location.pathname });

  if (me.isPending) {
    return (
      <div className={styles.centre}>
        <Stack gap={3} className={styles.panel}>
          <Skeleton text width="40%" />
          <Skeleton height="var(--space-8)" />
          <Skeleton height="var(--space-8)" />
        </Stack>
      </div>
    );
  }

  if (me.error) {
    if (me.error instanceof RequestFailed && me.error.isUnauthenticated) {
      // The sales page is what a signed-out visitor lands on; every other
      // path (a bookmarked screen, `/login`) is a sign-in wall instead.
      return path === "/" ? <Homepage /> : <SignIn />;
    }

    return (
      <div className={styles.centre}>
        <ErrorState
          title="Could not load your account"
          body={me.error instanceof RequestFailed ? me.error.message : "Something went wrong."}
          onRetry={() => void me.refetch()}
        />
      </div>
    );
  }

  const active = me.data.households[0];

  /**
   * Signed in with no household. Real, and not an error: an invited person
   * whose invitation has not been accepted yet lands exactly here.
   */
  if (!active) {
    return (
      <div className={styles.centre}>
        <FirstHousehold onCreated={() => void me.refetch()} />
      </div>
    );
  }

  const session: Session = {
    user: me.data.user,
    householdId: active.householdId,
    householdName: active.name,
    role: active.role,
    households: me.data.households,
  };

  return <SessionProvider value={session}>{children}</SessionProvider>;
}

/**
 * Signed in with no household. Two real cases, and neither is an error: the
 * first person to sign up, who has to create one, and an invited person whose
 * link has not been opened yet.
 */
function FirstHousehold({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create() {
    setBusy(true);
    setError(null);

    try {
      await request(Household, "/api/households", { method: "POST", body: { name } });
      onCreated();
    } catch (failure) {
      setError(
        failure instanceof RequestFailed ? failure.message : "That household was not created.",
      );
      setBusy(false);
    }
  }

  return (
    <Card title="Set up your household" className={styles.panel}>
      <Stack gap={4}>
        {error && <Alert tone="danger">{error}</Alert>}

        <Text tone="secondary">
          A household owns the accounts, the transactions and the rules. If someone has invited you
          to theirs, open the link they sent instead.
        </Text>

        <Field label="Name">
          {(props) => (
            <Input
              {...props}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Home"
            />
          )}
        </Field>

        <Button
          variant="primary"
          loading={busy}
          disabled={name.trim() === ""}
          onClick={() => void create()}
        >
          Create it
        </Button>
      </Stack>
    </Card>
  );
}

export function NotFound() {
  return (
    <div className={styles.centre}>
      <Stack gap={3}>
        <Heading level={1} size="xl">
          That page does not exist
        </Heading>
        <Text tone="secondary">Check the address, or go back to the dashboard.</Text>
      </Stack>
    </div>
  );
}
